import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';

export interface MeterQrPrintItem {
  meterCode: string;
  qrValue: string;
}

type PrinterPaperSize = 'A4' | '80MM' | '58MM';

interface UtilityMeterQrPrintModalProps {
  isOpen: boolean;
  meters: MeterQrPrintItem[];
  onClose: () => void;
  t: (key: string) => string;
}

const PRINTER_OPTIONS: Array<{
  value: PrinterPaperSize;
  widthMm: number;
  columns: number;
  labelKey: string;
}> = [
  { value: 'A4', widthMm: 210, columns: 4, labelKey: 'meterQrPrinterA4' },
  { value: '80MM', widthMm: 80, columns: 2, labelKey: 'meterQrPrinter80mm' },
  { value: '58MM', widthMm: 58, columns: 1, labelKey: 'meterQrPrinter58mm' },
];

function chunkRows<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

function getScreenColumnCount(count: number): number {
  if (count <= 0) return 1;
  if (count <= 10) return count;
  return Math.ceil(count / Math.ceil(count / 10));
}

export const UtilityMeterQrPrintModal: React.FC<UtilityMeterQrPrintModalProps> = ({
  isOpen,
  meters,
  onClose,
  t,
}) => {
  const [qrImages, setQrImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [paperSize, setPaperSize] = useState<PrinterPaperSize>('A4');

  const printerConfig = PRINTER_OPTIONS.find((opt) => opt.value === paperSize) ?? PRINTER_OPTIONS[0];
  const screenColumns = getScreenColumnCount(meters.length);
  const printRows = chunkRows(meters, printerConfig.columns);

  const meterKeys = useMemo(
    () => meters.map((m) => `${m.meterCode}::${m.qrValue}`),
    [meters],
  );

  useEffect(() => {
    if (!isOpen || meters.length === 0) {
      setQrImages({});
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const entries = await Promise.all(
          meters.map(async (meter) => {
            const key = `${meter.meterCode}::${meter.qrValue}`;
            const dataUrl = await QRCode.toDataURL(meter.qrValue, {
              width: 512,
              margin: 1,
              errorCorrectionLevel: 'M',
            });
            return [key, dataUrl] as const;
          }),
        );
        if (!cancelled) {
          setQrImages(Object.fromEntries(entries));
        }
      } catch {
        if (!cancelled) {
          setQrImages({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, meterKeys, meters]);

  const handlePrint = () => {
    window.print();
  };

  const renderCell = (meter: MeterQrPrintItem, compactLabel = false) => {
    const key = `${meter.meterCode}::${meter.qrValue}`;
    const src = qrImages[key];
    return (
      <div
        key={key}
        className="utility-meter-qr-cell flex flex-col items-center justify-start box-border p-1 min-w-0 border border-dashed border-gray-500 print:border-solid print:!border-black"
      >
        {src ? (
          <img
            src={src}
            alt={meter.meterCode}
            className="w-full h-auto object-contain"
          />
        ) : (
          <div className="w-full aspect-square bg-gray-100" />
        )}
        <p
          className={`mt-1 w-full text-center font-medium text-gray-800 break-all leading-tight ${
            compactLabel ? 'text-[10px]' : 'text-xs md:text-sm'
          }`}
        >
          {meter.meterCode}
        </p>
      </div>
    );
  };

  if (!isOpen) {
    return null;
  }

  const pageRule =
    paperSize === 'A4'
      ? 'size: A4 portrait; margin: 4mm;'
      : `size: ${printerConfig.widthMm}mm auto; margin: 2mm;`;

  return (
    <>
      <style>
        {`
          @media print {
            @page {
              ${pageRule}
            }
            html, body {
              width: ${printerConfig.widthMm}mm !important;
              height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
            }
            body > *:not(#utility-meter-qr-print-root) {
              display: none !important;
            }
            #utility-meter-qr-print-root {
              display: block !important;
              position: static !important;
              width: ${printerConfig.widthMm}mm !important;
              max-width: ${printerConfig.widthMm}mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              box-shadow: none !important;
            }
            #utility-meter-qr-print-root .utility-meter-qr-row {
              display: grid !important;
            }
            #utility-meter-qr-print-root .utility-meter-qr-cell {
              display: flex !important;
              flex-direction: column !important;
              break-inside: avoid;
              page-break-inside: avoid;
              border: 1px solid #000 !important;
              box-sizing: border-box !important;
            }
            #utility-meter-qr-print-root img {
              display: block !important;
            }
          }
        `}
      </style>

      <div className="fixed inset-0 z-[100] bg-white flex flex-col print:hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-800">{t('meterQrListTitle')}</h2>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-gray-600 flex items-center gap-2">
              <span>{t('meterQrPrinterSize')}</span>
              <select
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value as PrinterPaperSize)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                {PRINTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-sm text-gray-500">
              {meters.length} {t('meterCode')}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 p-4 md:p-6">
          <div className="w-full bg-white shadow-sm p-2">
            {loading ? (
              <div className="py-24 text-center text-gray-500">{t('loading')}</div>
            ) : meters.length === 0 ? (
              <div className="py-24 text-center text-gray-500">{t('noData')}</div>
            ) : (
              <div
                className="grid gap-0 w-full"
                style={{
                  gridTemplateColumns: `repeat(${screenColumns}, minmax(0, 1fr))`,
                }}
              >
                {meters.map((meter) => renderCell(meter))}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={loading || meters.length === 0}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {t('print')}
          </button>
        </div>
      </div>

      {createPortal(
        <div id="utility-meter-qr-print-root" className="hidden print:block">
          {!loading && meters.length > 0 && (
            <div className="w-full">
              {printRows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="utility-meter-qr-row grid w-full gap-0"
                  style={{
                    gridTemplateColumns: `repeat(${printerConfig.columns}, minmax(0, 1fr))`,
                  }}
                >
                  {row.map((meter) => renderCell(meter, paperSize !== 'A4'))}
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};
